import React, { useEffect, useState } from 'react';
import { Modal, Typography, Card, Tag, Space, Row, Col, Empty, Button, Form, Select, message, Input, Spin } from 'antd';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { 
  VideoCameraOutlined, 
  GlobalOutlined, 
  HomeOutlined, 
  QuestionOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  BookOutlined,
  EnvironmentOutlined,
  EditOutlined,
  CheckOutlined, 
  CloseOutlined,
  SaveOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import moment from 'moment-timezone';

const { Title, Text } = Typography;
const { Option } = Select;

// Time options 08:00–23:00 every 15 mins
const timeOptions = Array.from({ length: (23 - 8 + 1) * 4 }, (_, index) => {
  const hour = 8 + Math.floor(index / 4);
  const minutes = (index % 4) * 15;
  const label = `${hour.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;
  return { label, value: label + ":00" }; // stored as HH:mm:ss
});

// Hour options 0:15 → 3:00 (in minutes)
const hourOptions = Array.from({ length: 12 }, (_, index) => {
  const minutes = (index + 1) * 15; // 15, 30, …, 180
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return {
    label: `${h}h ${m.toString().padStart(2, "0")}m`,
    value: minutes
  };
});

const ClassDetailsModal = ({ visible, onClose, classData, teacherData, classChangeLogs, onRefresh }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // State for fetched data
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);

  // State for editing individual fields
  const [editingField, setEditingField] = useState(null);
  const [saving, setSaving] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch all required data
  useEffect(() => {
    if (visible) {
      fetchAllData();
    }
  }, [visible]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all data in parallel
      const [teachersRes, subjectsRes, classroomsRes, branchesRes] = await Promise.all([
        axios.get(`${API_URL}/teachers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/subjects`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/classrooms`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/branch`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setTeachers(teachersRes.data.teachers || []);
      
      // Handle subjects response - it might be nested in .data or direct array
      const subjectsData = subjectsRes.data?.subjects || subjectsRes.data || [];

      setSubjects(subjectsData);
      
      // Filter and sort classrooms
      const availableClassrooms = (classroomsRes.data || [])
        .filter(classroom => classroom.availability === true)
        .sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true, sensitivity: 'base' }));
      
      setClassrooms(availableClassrooms);
      setBranches(branchesRes.data || []);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get teacher name
  const getTeacherName = () => {
    if (!teacherData) return '-';
    return `${teacherData.nickname || ''} ${teacherData.firstname || ''} ${teacherData.lastname || ''}`.trim();
  };



  // Get student confirmation status tag
  const getConfirmationTag = (studentConfirm) => {
    const isConfirmed = studentConfirm === true || studentConfirm === 'true';
    return (
      <Tag color={isConfirmed ? 'green' : 'default'} icon={isConfirmed ? <CheckOutlined /> : <CloseOutlined />}>
        {isConfirmed ? (t('Confirmed') || 'Student Confirmed') : (t('Not Confirmed') || 'Student Not Confirmed')}
      </Tag>
    );
  };

  const formatPlatform = (platform) => {
    switch (platform) {
      case 'online':
        return (
          <Space>
            <VideoCameraOutlined style={{ color: '#1890ff' }} /> {t('online')}
          </Space>
        );
      case 'onsite':
        return (
          <Space>
            <GlobalOutlined style={{ color: '#52c41a' }} /> {t('onsite')}
          </Space>
        );
      case 'athome':
        return (
          <Space>
            <HomeOutlined style={{ color: '#fa8c16' }} /> {t('athome')}
          </Space>
        );
      default:
        return (
          <Space>
            <QuestionOutlined style={{ color: '#d9d9d9' }} /> -
          </Space>
        );
    }
  };

  // Format date only
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    try {
      const date = moment(dateString);
      if (date.isValid()) {
        return date.format('DD MMM YYYY');
      }
      return '-';
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  // Format time only
  const formatTime = (timeString) => {
    if (!timeString) return '-';
    
    try {
      const time = moment(timeString, 'HH:mm:ss');
      if (time.isValid()) {
        return time.format('HH:mm');
      }
      return timeString;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  // Format duration to "2:30hrs" format
  const formatDuration = (durationString) => {
    if (!durationString) return '-';
    
    try {
      // If it's already a number (integer from database), use it directly
      if (typeof durationString === 'number') {
        const totalMinutes = durationString;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
      }
      
      // Handle format like "2h30m" or "2 30"
      if (typeof durationString === 'string') {
        if (durationString.includes('h') && durationString.includes('m')) {
          const hours = parseInt(durationString.split('h')[0]);
          const minutes = parseInt(durationString.split('h')[1].replace('m', ''));
          return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
        }
        
        // Handle format like "2 30" (hours and minutes with space)
        if (durationString.includes(' ')) {
          const [hours, minutes] = durationString.split(' ').map(part => parseInt(part));
          return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
        }
        
        // Try to convert string to number (total minutes)
        const totalMinutes = parseInt(durationString);
        if (!isNaN(totalMinutes)) {
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
        }
      }
      
      // If we can't parse it, return as is
      return durationString;
    } catch (error) {
      console.error('Error formatting duration:', error);
      return durationString || '-';
    }
  };

  // Handle edit button click
  const handleEditClick = (fieldName) => {
    setEditingField(fieldName);
    
    // Set form values based on the field being edited
    switch(fieldName) {
      case 'teacher':
        form.setFieldsValue({ teacher_id: classData.teacher_id });
        break;
      case 'subject':
        form.setFieldsValue({ subject_id: classData.subject_id });
        break;
      case 'date':
        form.setFieldsValue({ date: moment(classData.date).format('YYYY-MM-DD') });
        break;
      case 'time':
        form.setFieldsValue({ 
          schedule_time: classData.schedule_time
        });
        break;
      case 'duration':
        // schedule_hour is stored as integer (total minutes in database)
        const scheduleHour = classData.schedule_hour || 60; // default 60 minutes
        const totalMinutes = typeof scheduleHour === 'number' ? scheduleHour : parseInt(scheduleHour) || 60;
        
        form.setFieldsValue({ 
          duration_minutes: totalMinutes
        });
        break;
      case 'platform':
        form.setFieldsValue({ platform: classData.platform });
        break;
      case 'classroom':
        form.setFieldsValue({
          classroom_id: Number(classData.classroom_id),   // must match option value type
          branch_id: Number(classData.branch_id)
        });
        break;
      default:
        break;
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingField(null);
    form.resetFields();
  };

  // Handle save individual field
  const handleSaveField = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const values = await form.validateFields();
      
      let updateData = {};
      
      // Prepare update data based on the field being edited
      switch(editingField) {
        case 'teacher':
          updateData = { teacher_id: values.teacher_id };
          break;
        case 'subject':
          updateData = { subjectid: values.subjectid || values.subject_id };
          break;
        case 'date':
          updateData = { date: values.date };
          break;
        case 'time':
          // Calculate end_time based on start time and duration
          const startTime = moment(values.schedule_time, 'HH:mm:ss');
          const scheduleHour = classData.schedule_hour || 60; // default 1 hour in minutes
          const endTime = startTime.clone().add(scheduleHour, 'minutes');
          
          updateData = { 
            schedule_time: values.schedule_time,
            end_time: endTime.format('HH:mm:ss')
          };
          break;
        case 'duration':
          const durationMinutes = values.duration_minutes;
          
          updateData = { 
            schedule_hour: durationMinutes  // Send as integer (total minutes)
          };
          
          // Also update end_time based on current start time and new duration
          if (classData.schedule_time) {
            const currentStartTime = moment(classData.schedule_time, 'HH:mm:ss');
            const newEndTime = currentStartTime.clone().add(durationMinutes, 'minutes');
            updateData.end_time = newEndTime.format('HH:mm:ss');
          }
          break;
        case 'platform':
          updateData = { platform: values.platform };
          break;
        case 'classroom':
          updateData = { 
            classroom_id: values.classroom_id,
            branch_id: values.branch_id
          };
          break;
        default:
          break;
      }

      // Make API call to update the class
      await axios.put(
        `${API_URL}/classes/${classData.classid}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      message.success(`${editingField.charAt(0).toUpperCase() + editingField.slice(1)} updated successfully`);
      setEditingField(null);
      form.resetFields();
      
      // Refresh parent component data
      if (onRefresh) {
        await onRefresh();
      }
      
    } catch (error) {
      console.error('Error updating class:', error);
      message.error('Failed to update class');
    } finally {
      setSaving(false);
    }
  };

  const InfoCard = ({ icon, title, value, color = '#1890ff', fieldName, editable = false }) => (
    <Card 
      size="small" 
      style={{ 
        height: '100%',
        borderLeft: `4px solid ${color}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
    >
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Space>
            {React.cloneElement(icon, { style: { color, fontSize: '16px' } })}
            <Text strong style={{ color: '#595959' }}>{title}</Text>
          </Space>
          {editable && editingField !== fieldName && (
            <Button 
              type="text" 
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditClick(fieldName)}
            />
          )}
        </Space>
        
        {editingField === fieldName ? (
          <div style={{ width: '100%' }}>
            {renderEditForm(fieldName)}
            <Space style={{ marginTop: '8px' }}>
              <Button 
                type="primary" 
                size="small"
                icon={<SaveOutlined />}
                onClick={handleSaveField}
                loading={saving}
              >
                Save
              </Button>
              <Button 
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
            </Space>
          </div>
        ) : (
          <div style={{ fontSize: '16px', fontWeight: '500', color: '#262626' }}>
            {value}
          </div>
        )}
      </Space>
    </Card>
  );

  // Render edit form based on field type
  const renderEditForm = (fieldName) => {
    switch(fieldName) {
      case 'teacher':
        return (
          <Form.Item name="teacher_id" style={{ marginBottom: 0 }}>
            <Select
              showSearch
              placeholder="Select Teacher"
              optionFilterProp="label"
              filterOption={(input, option) => {
                const label = option?.label || '';
                return label.toLowerCase().includes(input.toLowerCase());
              }}
              options={teachers.map(teacher => ({
                value: teacher.userid,
                label: `${teacher.nickname} ${teacher.firstname} ${teacher.lastname}`.trim()
              }))}
            />
          </Form.Item>
        );
      
      case 'subject':
        return (
          <Form.Item name="subject_id" style={{ marginBottom: 0 }}>
            <Select
              showSearch
              placeholder="Select Subject"
              optionFilterProp="label"
              filterOption={(input, option) => {
                const label = option?.label || '';
                return label.toLowerCase().includes(input.toLowerCase());
              }}
              options={subjects.map(subject => ({
                value: subject.subjectid,
                label: subject.title
              }))}

            />
          </Form.Item>
        );
      
      case 'date':
        return (
          <Form.Item name="date" style={{ marginBottom: 0 }}>
            <Input 
              type="date" 
              style={{ width: '100%' }}
            />
          </Form.Item>
        );
      
      case 'time':
        return (
          <Form.Item name="schedule_time" label="Start Time" style={{ marginBottom: 0 }}>
            <Select
              showSearch
              placeholder="Select Time"
              style={{ width: '100%' }}
            >
              {timeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        );
      
      case 'duration':
        return (
          <Form.Item name="duration_minutes" label="Duration" style={{ marginBottom: 0 }}>
            <Select
              placeholder="Select Duration"
              style={{ width: '100%' }}
            >
              {hourOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        );
      
      case 'platform':
        return (
          <Form.Item name="platform" style={{ marginBottom: 0 }}>
            <Select>
              <Option value="online">
                <Space>
                  <VideoCameraOutlined /> {t('online')}
                </Space>
              </Option>
              <Option value="onsite">
                <Space>
                  <GlobalOutlined /> {t('onsite')}
                </Space>
              </Option>
              <Option value="athome">
                <Space>
                  <HomeOutlined /> {t('athome')}
                </Space>
              </Option>
            </Select>
          </Form.Item>
        );
      
      case 'classroom':
        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Form.Item name="branch_id" label="Branch" style={{ marginBottom: 8 }}>
              <Select
                showSearch
                placeholder="Select Branch"
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = option?.label || '';
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                options={branches.map(branch => ({
                  value: branch.id,
                  label: branch.name
                }))}
              />
            </Form.Item>
            <Form.Item name="classroom_id" label="Classroom" style={{ marginBottom: 0 }}>
              <Select
                showSearch
                placeholder="Select Classroom"
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = option?.label || '';
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                options={classrooms.map(room => ({
                  value: room.classroomid,   // <-- correct primary key
                  label: room.number         // A, B, C, etc.
                }))}

              />
            </Form.Item>
          </Space>
        );
      
      default:
        return null;
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width={900}
      footer={null}
      centered
      style={{ top: 20 }}
    >
      <Spin spinning={loading}>
        {classData ? (
          <div>
            <Form form={form}>
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Title level={2} style={{ marginBottom: '8px', color: '#1890ff' }}>
                  {classData.subject_name}
                </Title>
                <Space size="large">
                  {getConfirmationTag(classData.student_confirm)}
                  <Text type="secondary" style={{ fontSize: '14px' }}>
                    Class ID: {classData.classid}
                  </Text>
                  <Text type="secondary" style={{ fontSize: '14px' }}>
                    Course: {classData.class_code || '-'}
                  </Text>
                </Space>
              </div>

              {/* Class Details Cards */}
              <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} md={8}>
                  <InfoCard
                    icon={<UserOutlined />}
                    title="TEACHER"
                    value={getTeacherName()}
                    color="#722ed1"
                    fieldName="teacher"
                    editable={true}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <InfoCard
                    icon={<BookOutlined />}
                    title="SUBJECT"
                    value={classData.subject_name}
                    color="#eb2f96"
                    fieldName="subject"
                    editable={true}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <InfoCard
                    icon={<CalendarOutlined />}
                    title="DATE"
                    value={formatDate(classData.date)}
                    color="#52c41a"
                    fieldName="date"
                    editable={true}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <InfoCard
                    icon={<ClockCircleOutlined />}
                    title="TIME"
                    value={`${formatTime(classData.schedule_time)} - ${formatTime(classData.end_time)}`}
                    color="#fa8c16"
                    fieldName="time"
                    editable={true}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <InfoCard
                    icon={<BookOutlined />}
                    title="DURATION"
                    value={formatDuration(classData.schedule_hour || classData.duration)}
                    color="#13c2c2"
                    fieldName="duration"
                    editable={true}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <InfoCard
                    icon={<VideoCameraOutlined />}
                    title="PLATFORM"
                    value={formatPlatform(classData.platform)}
                    color="#1890ff"
                    fieldName="platform"
                    editable={true}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <InfoCard
                    icon={<EnvironmentOutlined />}
                    title="CLASSROOM"
                    value={classData.classroom_number || '-'}
                    color="#f5222d"
                    fieldName="classroom"
                    editable={true}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <InfoCard
                    icon={<EnvironmentOutlined />}
                    title="BRANCH"
                    value={classData.name || '-'}
                    color="#2f54eb"
                    editable={false}
                  />
                </Col>
              </Row>

              {/* Attendance: nickname + status, read-only */}
              {(() => {
                const studentIds = classData.student_ids
                  ? String(classData.student_ids).split(',').map((id) => id.trim()).filter(Boolean)
                  : [];
                const studentNames = classData.student_nicknames
                  ? String(classData.student_nicknames).split(',').map((n) => n.trim())
                  : [];

                const statusByStudent = {};
                (classData.attendance_statuses || []).forEach((a) => {
                  statusByStudent[a.student_id] = a.status;
                });

                const colorFor = (status) => {
                  if (status === 'Present') return 'green';
                  if (status === 'Late') return 'orange';
                  if (status === 'Absent') return 'red';
                  if (status === 'To be rescheduled') return 'purple';
                  return 'default';
                };

                const hasStudentField = 'student_ids' in (classData || {});

                return (
                  <Card
                    size="small"
                    style={{ marginBottom: '24px', borderLeft: '4px solid #13c2c2' }}
                  >
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text strong style={{ fontSize: '14px' }}>Attendance</Text>
                      {studentIds.length === 0 ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {hasStudentField
                            ? 'No registered students for this class.'
                            : '⚠️ student_ids field missing — restart backend to pick up SQL change.'}
                        </Text>
                      ) : (
                        <Space size={[8, 8]} wrap>
                          {studentIds.map((id, idx) => {
                            const sid = parseInt(id, 10);
                            const status = statusByStudent[sid];
                            return (
                              <Tag
                                key={sid}
                                color={colorFor(status)}
                                style={{ margin: 0, padding: '2px 10px' }}
                              >
                                {studentNames[idx] || `#${sid}`} — {status || 'No record'}
                              </Tag>
                            );
                          })}
                        </Space>
                      )}
                    </Space>
                  </Card>
                );
              })()}

              {/* Note Section */}
              <Card
                size="small"
                style={{
                  marginBottom: '24px',
                  backgroundColor: '#fffbe6',
                  borderLeft: '4px solid #faad14',
                }}
              >
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                    <Text strong style={{ fontSize: '14px' }}>
                      <BookOutlined style={{ marginRight: 8 }} />
                      Course Note
                    </Text>
  
                  </Space>
                  <Text style={{ whiteSpace: 'pre-line' }}>
                    {classData.book_details_materials || 'No notes available'}
                  </Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    This note belongs to the whole course — edit it from the Courses page.
                  </Text>

                </Space>
              </Card>
            </Form>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Empty description="No class details available" />
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default ClassDetailsModal;